import React, { useState } from 'react';
import ServerError from '../../../../../components/ServerError';
import PageHeader from '../../../components/PageHeader';
import SkeletonGraph from '../../../components/Skeleton/Graph';
import SkeletonPageHeader from '../../../components/Skeleton/PageHeader';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { useFlairBreadcrumbHook } from '../../../../../hooks/useFlairBreadcrumbHook';
import { EmployeeTurnoverRateTable } from './components/EmployeeTurnoverRateTable';
import { Card } from 'react-bootstrap';
import { SkeletonTable } from '../../../components/Skeleton/Table';
import {
  EmployeeTurnoverRatePeriodFilter,
  Period,
} from './components/EmployeeTurnoverRatePeriodFilter';
import { useMyEmployeeTurnoverRateReportData, prepareChartData } from './logic';
import { FlairLabel } from '../../../../../atomic/atoms/Flairlabel/FlairLabel';
import BarChart from '../charts/BarChart';
import { managerRoutes } from '../../../manager/routes';

const i18NavigationPath = 'navigation.menuItems.analytics';
const i18Path = 'analytics.employeeTurnoverRatePage';

export const EmployeeTurnoverRatePage: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const tNavigation = useNamespacedTranslation(i18NavigationPath);
  useFlairBreadcrumbHook([
    {
      label: tNavigation('title'),
      url: managerRoutes.analytics.route.create({}),
    },
    { label: tNavigation('submenuItems.employeeTurnoverRate') },
  ]);

  const t = useNamespacedTranslation(i18Path);

  const [period, setPeriod] = useState<Period>('last6Months');

  const { data, error, loading } = useMyEmployeeTurnoverRateReportData(period);

  if (error) {
    return <ServerError />;
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!data?.reports?.turnoverRate) {
    return null;
  }

  const { categories, seriesData } = prepareChartData(
    data.reports.turnoverRate,
  );

  return (
    <>
      <PageHeader title={t('title')} />
      <Card>
        <Card.Header className="small">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FlairLabel text={t('show')} className="h6-regular me-2-5" />
              <EmployeeTurnoverRatePeriodFilter
                value={period}
                onChange={setPeriod}
              />
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <BarChart
            seriesName={t('percentage')}
            seriesData={seriesData}
            categories={categories}
            height={520}
          />
          <EmployeeTurnoverRateTable
            categories={categories}
            turnoverRates={seriesData}
          />
        </Card.Body>
      </Card>
    </>
  );
};

const LoadingSkeleton = () => (
  <>
    <SkeletonPageHeader />
    <SkeletonGraph height={520} />
    <SkeletonTable rows={2} columns={6} />
  </>
);
